import { SendButton } from '@/components/ui/SendButton';
import { useIsPhone } from '@/mobile/layout';
/**
 * Feed pieces shared by Home on a computer (Feed.tsx) and the phone's cards (mobile/FeedCards.tsx), t184u.
 */
import { useEffect, useRef, useState } from 'react';
import type { FeedThread } from '@tradeoutlet/shared';
import { EmojiButton, insertAtCaret, restoreCaret } from '@/components/emoji/EmojiButton';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useUi } from '@/lib/store';
import { timeOfDay } from '@/lib/format';
import { RelativeTime } from '@/components/time/RelativeTime';
import { PersonButton } from '@/features/people/PersonDrawer';
import { enterToSend } from '@/lib/enterToSend';

/** The acknowledgement's discussion: its chat thread (replies to the message, or the thread it's in). */
export const ackThreadLink = (a: { conversation_id: string; parent_id: string | null; message_id: string }) => `/c/${a.conversation_id}?thread=${a.parent_id ?? a.message_id}`;

/** Task comments live outside chat: show them read-only with a quick reply box. */
export function TaskComments({ th, autoFocus, onSent }: { th: FeedThread; autoFocus?: boolean; onSent?: () => void }) {
  const toast = useUi(s => s.toast);
  const { userId } = useAuth();
  const phone = useIsPhone();
  const [draft, setDraft] = useState('');
  // t184u: what I've just posted shows under the others (the task's own comments; the full list is on the task).
  const [sent, setSent] = useState<FeedThread['comments']>([]);
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (autoFocus) box.current?.focus(); }, [autoFocus]);
  const send = async () => {
    const body = draft.trim(); if (!body) return;
    setDraft('');
    try { await api.addTaskComment(th.entity_id, { body }); if (onSent) onSent(); else setSent(s => [...s, { actor_id: userId, actor_name: 'You', body, at: new Date().toISOString() }]); }
    catch (e) { setDraft(body); toast(errorMessage(e), 'error'); }
  };
  th = sent.length ? { ...th, comments: [...sent].reverse().concat(th.comments) } : th;
  return (
    <div className="mt-2 space-y-1.5">
      {[...(th.events ?? []).map(e => ({ ...e, event: true as const })), ...th.comments.map(c => ({ ...c, event: false as const }))].sort((a, b) => (a.at < b.at ? -1 : 1)).map((c, i) => c.event ? (
        <p key={i} data-testid="thread-event" className="flex items-start gap-2 px-3 text-xs text-slate-500 dark:text-slate-400">
          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden />
          <span className="min-w-0 flex-1 leading-5">{c.actor_id ? <PersonButton userId={c.actor_id} name={c.actor_name} className="rounded font-medium text-slate-700 hover:underline dark:text-slate-200">{c.actor_name}</PersonButton> : <span className="font-medium">{c.actor_name}</span>} {c.text}
            <time dateTime={c.at} className="ml-1.5 whitespace-nowrap text-slate-400 dark:text-slate-500">· {timeOfDay(c.at)}</time></span>
        </p>
      ) : (
        <p key={i} className="rounded-lg bg-slate-50 px-3 py-1.5 text-sm dark:bg-slate-800/60"><PersonButton userId={c.actor_id} name={c.actor_name} className="rounded font-semibold hover:underline">{c.actor_name}</PersonButton> <span className="whitespace-pre-wrap">{c.body}</span> <span className="text-xs text-slate-500"><RelativeTime iso={c.at} /></span></p>
      ))}
      {th.reply && (
        <form className="flex items-end gap-1.5" onSubmit={e => { e.preventDefault(); void send(); }}>
          <label className="sr-only" htmlFor={`tc-${th.entity_id}`}>Reply</label>
          <textarea ref={box} id={`tc-${th.entity_id}`} rows={1} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Reply…"
            enterKeyHint="send" onKeyDown={enterToSend({ value: draft, onSend: () => void send() })}
            className="min-h-8 flex-1 resize-none rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900" />
          <EmojiButton onPick={e => { const r = insertAtCaret(box.current, draft, e); setDraft(r.value); restoreCaret(box.current, r.caret); }} />
          <SendButton phone={phone} type="submit" label="Send reply" disabled={!draft.trim()} />
        </form>
      )}
    </div>
  );
}


/**
 * t277u: one calm line in the feed above the first item you'd already have seen on your last visit. The feed is
 * newest first, so the label describes the posts BELOW it ("Earlier · already seen"); everything above is new.
 */
export function FeedNewDivider() {
  return (
    <div role="separator" aria-label="Earlier · already seen" className="flex items-center gap-3 py-1 text-xs text-slate-500 dark:text-slate-400" data-testid="feed-new-divider">
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" aria-hidden />
      <span>Earlier · already seen</span>
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" aria-hidden />
    </div>
  );
}
